import { Controller } from "@hotwired/stimulus"

// Tap a day to open it — the touch equivalent of the hover expansion on a
// wide screen.
//
// The wide layout reveals the meals and macro detail on hover, which a phone
// cannot do. So below that breakpoint the same detail is a tap away instead
// of being unreachable: a stacked card has the room, it just needs asking
// for.
//
// The class goes on the same wrapper :hover is matched against, so one set of
// CSS rules serves both — see .is-expanded beside the :hover selectors.
export default class extends Controller {
  // The width the hover layout takes over at, matching the CSS.
  static WIDE = "(min-width: 80rem)"

  connect() {
    this.query = window.matchMedia(this.constructor.WIDE)
    this.onChange = () => this.collapseIfWide()
    this.query.addEventListener("change", this.onChange)
  }

  disconnect() {
    this.query.removeEventListener("change", this.onChange)
  }

  toggle(event) {
    // NOT WHERE HOVER ALREADY DOES IT. This is the touch route to a detail
    // the wide layout reveals on hover — so on a wide screen a click set
    // .is-expanded, the pointer left, and the day stayed expanded: macro
    // lines up, and the 4.5rem of right padding reserved for the hover
    // controls still held, so the cards below could not resize.
    //
    // Nothing took the class off again — collapseIfWide only fires when the
    // media query CHANGES, and it had not. Checked at the click rather than
    // on connect, because a window can be resized without either.
    if (this.query.matches) return

    // The header carries a copy button, and the card below it is full of
    // meal links. A tap that lands on any control belongs to that control,
    // not to the card.
    //
    // EXCEPT THE DAY NAME, which is a link to that day's page and sits
    // exactly where anybody reaches to open the card — so the first tap
    // was swallowed by a control nobody was aiming at, and only a second
    // one landing on empty space did anything. Cody, 12 Sep, three rounds
    // of "still have to tap twice".
    //
    // Below the hover breakpoint the card's own job is to open; the day
    // page is still one tap away once it is open. data-day-link marks it so
    // this knows which link to make an exception of rather than guessing
    // from the href.
    const control = event.target.closest("a, button, input, select, label, [role='button']")
    if (control && !control.hasAttribute("data-day-link")) return

    // The day name is a link, so the navigation has to be called off — the
    // tap was for the card.
    if (control) event.preventDefault()

    this.element.classList.toggle("is-expanded")
  }

  // A day tapped open on a phone must not stay open once the window is wide
  // enough to hover.
  //
  // Leaving it set seemed more predictable — you asked for it open, so it
  // stays open. In practice the class outlives the layout that gave it
  // meaning: at seven columns the day is stuck expanded, hover cannot take
  // over, and the only way out is to find and tap the same card again.
  collapseIfWide() {
    if (this.query.matches) {
      this.element.classList.remove("is-expanded")
    }
  }
}
